import { useCallback, useEffect, useRef } from 'react';

export type PageLeaveType = 'visibilitychange' | 'pagehide' | 'beforeunload';

type PageLeaveCallback = (leaveType: PageLeaveType) => void;

/**
 * 브라우저 이탈을 감지하는 훅.
 *
 * 3중 레이어로 감지하며, 한 번의 이탈에 대해 콜백을 1회만 호출한다.
 * - `visibilitychange` (hidden): 모든 플랫폼에서 가장 먼저, 가장 신뢰도 높게 발화
 * - `pagehide`: bfcache 호환 fallback
 * - `beforeunload`: 레거시 브라우저 fallback
 *
 * `visibilitychange`로 다시 visible이 되면 리셋되어 재감지 가능.
 */
const usePageLeave = (callback: PageLeaveCallback) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const hasFiredRef = useRef(false);

  const handleLeave = useCallback((leaveType: PageLeaveType) => {
    if (hasFiredRef.current) return;
    hasFiredRef.current = true;
    callbackRef.current(leaveType);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleLeave('visibilitychange');
      } else {
        // visible로 복귀 시 리셋하여 재감지 가능하도록 함
        hasFiredRef.current = false;
      }
    };

    const handlePageHide = () => {
      handleLeave('pagehide');
    };

    const handleBeforeUnload = () => {
      handleLeave('beforeunload');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleLeave]);
};

export default usePageLeave;
